import { type ReactNode } from 'react';
import { Link as RouterLink, useLocation, useParams } from 'react-router-dom';

import Tab from '@liquidweb-dev/undercurrent/Tab';
import Tabs from '@liquidweb-dev/undercurrent/Tabs';
import Button from '@liquidweb-dev/undercurrent/Button';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import FolderIcon from '@liquidweb-dev/undercurrent/icons/lucide/Folder';
import Skeleton from '@liquidweb-dev/undercurrent/Skeleton';
import EmptyState from '@liquidweb-dev/undercurrent/EmptyState';
import CircleXIcon from '@liquidweb-dev/undercurrent/icons/lucide/CircleX';
import List from '@liquidweb-dev/undercurrent/List';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';
import { type ListProps } from '@liquidweb-dev/undercurrent/List';

import PageLayout, { PageLayoutInsetContent } from 'components/PageLayout';
import TabBar from 'components/TabBar';
import useAssetDetails from 'modules/queries/asset/useDetails';
import {
	paths,
	useNavActivePath,
	useNavTitle,
} from 'components/LeftHandLayout';
import NotFound from 'pages/NotFound';
import FullScreenError from 'components/legacy/FullScreenError';
import { getErrorStringHelper } from 'utility/redux/selectorHelperFunctions/apiModules';
import useDocumentTitle from 'utility/hooks/useDocumentTitle';

interface IPPoolDetailsLayoutProps {
	children: ReactNode;
}

const Skeletons = () => (
	<>
		<Skeleton variant="rounded" width="100%" height="200px" />
		<Skeleton variant="rounded" width="100%" height="400px" />
		<Skeleton variant="rounded" width="100%" height="150px" />
	</>
);

const Loading = () => {
	return (
		<PageLayout title="IP Pool Details" isLoading>
			<PageLayoutInsetContent sx={{ flexGrow: 1, gap: 300 }}>
				<Skeletons />
			</PageLayoutInsetContent>
		</PageLayout>
	);
};

const Error = ({ sx, ...rest }: ListProps) => {
	return (
		<PageLayout title="IP Pool Details">
			<PageLayoutInsetContent>
				<List
					sx={combineSx(
						{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						},
						sx,
					)}
					{...rest}
				>
					<EmptyState
						icon={
							<CircleXIcon
								sx={{
									color: ({ vars }) => vars.palette.uc.danger.icon.main,
									height: ({ size }) => size.md,
									width: ({ size }) => size.md,
								}}
							/>
						}
					>
						It looks like we ran into a problem loading this information
					</EmptyState>
				</List>
			</PageLayoutInsetContent>
		</PageLayout>
	);
};

/**
 * A layout for IP Pool details pages.
 */
const IPPoolDetailsLayout = ({ children }: IPPoolDetailsLayoutProps) => {
	const { uniqId } = useParams<{ uniqId: string }>();
	const { pathname } = useLocation();

	useNavTitle('IP Pool');
	useNavActivePath(paths.CloudIPAddresses);

	const {
		data: ipPoolAsset,
		isLoading: isLoadingAssetDetails,
		isError: isAssetDetailsError,
		error: assetDetailsError,
	} = useAssetDetails(
		{
			uniq_id: uniqId,
			alsowith: ['featureDetails', 'zone'],
		},
		{
			enabled: Boolean(uniqId),
		},
	);

	useDocumentTitle(ipPoolAsset?.domain || 'IP Pool');

	if (isLoadingAssetDetails) {
		return <Loading />;
	}

	if (isAssetDetailsError && assetDetailsError.statusCode === 404) {
		return <NotFound />;
	}

	if (isAssetDetailsError) {
		return (
			<FullScreenError content={getErrorStringHelper(assetDetailsError)} />
		);
	}

	if (!ipPoolAsset?.domain) {
		return <Error />;
	}

	const { domain, project_name: projectName } = ipPoolAsset;

	// Extract tab from URL pathname
	// /network/ip-pools/:uniqId -> 'overview'
	// /network/ip-pools/:uniqId/activity -> 'activity'
	const pathSegments = pathname.split('/');
	const tabsValue = pathSegments[4] || 'overview';

	return (
		<PageLayout
			title={domain}
			isLoading={isLoadingAssetDetails}
			description={
				projectName ? (
					<Stack
						direction="row"
						sx={{
							gap: 50,
							alignItems: 'center',
						}}
					>
						<FolderIcon
							sx={({ size }) => ({
								width: size.xS,
								height: size.xS,
							})}
						/>
						<Typography
							variant="label-lg"
							sx={{
								fontWeight: 500,
							}}
						>
							{projectName}
						</Typography>
					</Stack>
				) : undefined
			}
		>
			<TabBar sx={{ marginBottom: 600 }} isLoading={isLoadingAssetDetails}>
				<Tabs value={tabsValue}>
					<Tab
						component={RouterLink}
						value="overview"
						to={`/network/cloud-ip-addresses/${uniqId}`}
						label="Overview"
					/>
					<Tab
						component={RouterLink}
						value="activity"
						to={`/network/cloud-ip-addresses/${uniqId}/activity`}
						label="Activity"
					/>
				</Tabs>
				<Button
					component="a"
					href="https://docs.nexcess.com/hosting/hosting-services/cloud-vps/creating-an-ip-pool-for-your-cloud-vps/"
					target="_blank"
					rel="noopener noreferrer"
					variant="ghost"
					size="large"
				>
					Docs
				</Button>
			</TabBar>
			<PageLayoutInsetContent sx={{ flexGrow: 1 }}>
				{children}
			</PageLayoutInsetContent>
		</PageLayout>
	);
};

export default IPPoolDetailsLayout;
